import { useState, useEffect } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { LoggedInAccount } from "../../Interfaces/AccountUsers";
import {
  getCurrentlyLoggedInUser,
  parseParticipantID,
} from "../../CommonFunctions";
import config from "../../config";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import ParticipantDashboardMobileView from "./ParticipantDashboardViews/MobileDashboard";
import ParticipantDashboardDesktopView from "./ParticipantDashboardViews/DesktopDashboard";
import { ImprovedParticipantDashboard } from "../../Interfaces/FundraiserRole";
import UnauthroizedParticipant from "../Dashboards/Participant/UnauthorizedParticipant";

const RefactoredParticipantDashboard = () => {
  const isMobile = useIsMobile();
  const [accountID, setAccountID] = useState(null);
  const [error, setError] = useState("");
  const [loggedInAccount, setLoggedInAccount] =
    useState<LoggedInAccount | null>(null);
  const [participantDashboard, setParticipantDashboard] =
    useState<ImprovedParticipantDashboard>();
  const { linkExtension } = useParams();
  const [loading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchParticipantDetails = async () => {
      if (accountID) {
        setLoading(true);
        const token = localStorage.getItem("accessToken");

        if (!token) {
          setError("User is not authenticated");
          setLoading(false);
          return;
        }
        try {
          const [response] = await Promise.all([
            fetch(
              `${config.API_BASE_URL}fundraiser-role/participant-dashboard/${parseParticipantID(linkExtension || "")}/`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              },
            ),
            new Promise((resolve) => setTimeout(resolve, 300)),
          ]);
          if (response.ok) {
            const data = await response.json();
            setParticipantDashboard(data);
          } else {
            const errorData = await response.json();
            setError(errorData.detail || "Failed to fetch active fundraisers");
          }
        } catch (err) {
          setError("An error occurred. Please try again.");
        } finally {
          setLoading(false);
        }
      }
    };
    fetchParticipantDetails();
  }, [accountID, linkExtension]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const user = await getCurrentlyLoggedInUser();
        setLoggedInAccount(user);
      } catch (error) {
        navigate("/login");
      }

      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("User is not authenticated");
        return;
      }

      try {
        const response = await fetch(
          `${config.API_BASE_URL}account/user-details/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setAccountID(data.account);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || "Failed to fetch user details");
        }
      } catch (err) {
        setError("An error occurred. Please try again.");
      }
    };

    fetchUserDetails();
    document.title = "User Dashboard";
  }, [accountID, navigate]);

  // check to ensure the account profile being loaded is attached to the account signed in
  const accountProfileNotInAccount = !(
    loggedInAccount &&
    participantDashboard &&
    loggedInAccount.account_profiles.includes(
      participantDashboard.fundraiser_info.participant_account_profile_id,
    )
  );

  return loading ? (
    <LoadingSpinner textToDisplay="Loading Participant Dashboard" />
  ) : accountProfileNotInAccount ? (
    <UnauthroizedParticipant />
  ) : (
    <ParticipantDashboardDesktopView
      participantDashboard={participantDashboard}
      loggedInUser={loggedInAccount}
      isMobile={isMobile}
    />
  );
};

export default RefactoredParticipantDashboard;
